import { Radio } from 'lucide-react';

import { useEnvironmentRegistries } from '@/react/portainer/environments/queries/useEnvironmentRegistries';
import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';
import { url } from '@/react/portainer/registries/ListView/RegistriesDatatable/columns/url';
import { AddButton } from '@/react/portainer/registries/ListView/RegistriesDatatable/AddButton';
import { AppliedPolicy } from '@/react/portainer/environments/types';
import { useCanManagePolicies } from '@/react/portainer/policies/utils/useCanManagePolicies';

import { Datatable } from '@@/datatables';
import { createPersistedStore } from '@@/datatables/types';
import { useTableState } from '@@/datatables/useTableState';
import { LinkButton } from '@@/LinkButton';

import { name } from './columns/name';
import { actions } from './columns/actions';

function getColumns(hasRegistryPolicyApplied: boolean) {
  return [name, url, actions(hasRegistryPolicyApplied)];
}

const tableKey = 'registries';

const store = createPersistedStore(tableKey);

export function EnvironmentRegistriesDatatable({
  registryPolicy,
}: {
  registryPolicy?: AppliedPolicy;
}) {
  const environmentId = useEnvironmentId();
  const query = useEnvironmentRegistries(environmentId);

  const hasRegistryPolicyApplied = !!registryPolicy?.policyID;
  const canManagePolicies = useCanManagePolicies();
  const policyName = registryPolicy?.policyName || '';
  const policyId = registryPolicy?.policyID || 0;

  const tableState = useTableState(store, tableKey);

  return (
    <Datatable
      columns={getColumns(hasRegistryPolicyApplied)}
      dataset={query.data || []}
      isLoading={query.isLoading}
      settingsManager={tableState}
      title="Registries"
      titleIcon={Radio}
      renderTableActions={() => (
        <>
          {canManagePolicies && hasRegistryPolicyApplied && (
            <LinkButton
              to="portainer.policy"
              params={{ id: policyId, tab: 'config' }}
              data-cy="view-policy-link"
              className="w-fit h-fit"
              size="small"
              color="light"
            >
              Manage registry policy &apos;{policyName}&apos;
            </LinkButton>
          )}
          <AddButton />
        </>
      )}
      disableSelect
      data-cy="environment-registries-datatable"
    />
  );
}
